import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Typography, Button, Card, CardActionArea, Chip, CircularProgress } from '@mui/material'
import { imageUrl } from './reviewModel'
import { getPublishPieces } from './publishApi'

const STATE_COLORS = { ready: 'success', approved: 'info', blocked: 'error' }

// "Prontas para publicar": every piece whose shots are all decided and that is
// not on the site yet, each linking to its product-page preview.
export default function PublishList({ fetchResource }) {
    const { t } = useTranslation()
    const [pieces, setPieces] = useState(null)
    const [failed, setFailed] = useState(false)

    const load = useCallback(async () => {
        setFailed(false)
        const response = await getPublishPieces(fetchResource)
        if (response.error) {
            setFailed(true)
            return
        }
        setPieces(response.pieces)
    }, [fetchResource])

    useEffect(() => { load() }, [load])

    if (failed && pieces === null) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography color='text.secondary'>{t('photoReview.publish.list.loadFailed')}</Typography>
                <Button variant='outlined' onClick={load}>{t('photoReview.publish.list.tryAgain')}</Button>
            </Box>
        )
    }
    if (pieces === null) return <CircularProgress size={24} />

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 4 }}>
            <Typography>{t('photoReview.publish.list.count', { count: pieces.length })}</Typography>
            {pieces.length === 0 && <Typography color='text.secondary'>{t('photoReview.publish.list.empty')}</Typography>}
            {pieces.map(piece => (
                <Card key={piece.sku} variant='outlined'>
                    <CardActionArea component={RouterLink} to={`/photo-review/publish/${piece.sku}`} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 2, p: 1.5 }}>
                        <Box sx={{ width: 72, height: 72, flexShrink: 0, bgcolor: '#ffffff', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            {piece.thumbnail && (
                                <img
                                    src={imageUrl(piece.thumbnail.photoId, 'edited', piece.thumbnail.attempts, 'preview')}
                                    alt={t('photoReview.publish.list.thumbnailAlt', { sku: piece.sku })}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontWeight: 600 }}>{piece.sku}</Typography>
                                <Chip size='small' color={STATE_COLORS[piece.state]} variant={piece.state === 'blocked' ? 'outlined' : 'filled'} label={t(`photoReview.publish.list.state.${piece.state}`)} />
                                {piece.republish && <Chip size='small' color='warning' label={t('photoReview.republish.chip')} />}
                                {piece.shotCount > 0 && <Typography variant='body2' color='text.secondary'>{t('photoReview.publish.list.shots', { count: piece.shotCount })}</Typography>}
                            </Box>
                            {piece.title && <Typography variant='body2' noWrap>{piece.title}</Typography>}
                            {piece.blockers.length > 0 && (
                                <Box component='ul' sx={{ m: 0, mt: 0.5, pl: 2.5, color: 'error.main' }}>
                                    {piece.blockers.map(blocker => <Typography component='li' variant='body2' key={blocker}>{blocker}</Typography>)}
                                </Box>
                            )}
                            {piece.error && <Typography variant='body2' color='error.main' sx={{ mt: 0.5 }}>{t('photoReview.publish.list.lastError', { error: piece.error })}</Typography>}
                        </Box>
                    </CardActionArea>
                </Card>
            ))}
        </Box>
    )
}
